/**
 * Client-side Overview date-window helpers (trend chart / fallbacks).
 * Primary Overview KPIs and tables use backend `?range=7d|30d|6m|1y|all`.
 */

import { OVERVIEW_DATE_RANGE_DAYS } from './components/HeatmapChart';

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function severityBucket(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'critical') return 'critical';
  if (s === 'high') return 'high';
  if (s === 'medium') return 'medium';
  if (s === 'low' || s === 'log' || s === 'info' || s === 'informational') return 'low';
  return null;
}

function severityFloatOf(row) {
  const n = Number.parseFloat(row?.severity_float);
  if (Number.isFinite(n)) return n;
  const bucket = severityBucket(row?.severity);
  if (bucket === 'critical') return 9.5;
  if (bucket === 'high') return 7.5;
  if (bucket === 'medium') return 5.0;
  if (bucket === 'low') return 2.0;
  return -1;
}

export function findingDetectedMs(row) {
  const raw = row?.date_time ?? row?.dateTime ?? row?.detected_at ?? row?.created_time ?? '';
  const text = String(raw || '').trim();
  if (!text) return NaN;
  const isoDay = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) {
    const ms = Date.parse(`${isoDay[1]}T00:00:00`);
    return Number.isNaN(ms) ? NaN : ms;
  }
  const dmy = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) {
    const ms = Date.parse(`${dmy[3]}-${dmy[2]}-${dmy[1]}T00:00:00`);
    return Number.isNaN(ms) ? NaN : ms;
  }
  const ms = Date.parse(text);
  return Number.isNaN(ms) ? NaN : ms;
}

const API_RANGE_DAYS = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '6m': 182,
  '1y': 365,
};

export function overviewDaysParamToNumber(daysParam) {
  const raw = String(daysParam ?? '').trim().toLowerCase();
  if (!raw || raw === 'all' || raw === 'all_time') return null;
  if (Object.prototype.hasOwnProperty.call(API_RANGE_DAYS, raw)) {
    return API_RANGE_DAYS[raw];
  }
  const n = Number.parseInt(raw, 10);
  if (Number.isFinite(n) && n > 0) return n;
  return null;
}

export function filterFindingsByDays(rows, daysParam) {
  const list = Array.isArray(rows) ? rows : [];
  const days = overviewDaysParamToNumber(daysParam);
  if (days == null) return list;
  const cutoffMs = Date.now() - days * 86400000;
  return list.filter((row) => {
    const t = findingDetectedMs(row);
    if (Number.isNaN(t)) return false;
    return t >= cutoffMs;
  });
}

/** Normalize open/closed/accepted packs from getDashboardVulnerabilities. */
export function flattenOverviewFindingPacks({ open, closed, accepted } = {}) {
  const openRows = [
    ...(Array.isArray(open?.network) ? open.network.map((r) => ({ ...r, _scanner: 'network', _status: 'open' })) : []),
    ...(Array.isArray(open?.web) ? open.web.map((r) => ({ ...r, _scanner: 'web', _status: 'open' })) : []),
  ];
  const closedRows = [
    ...(Array.isArray(closed?.network) ? closed.network.map((r) => ({ ...r, _scanner: 'network', _status: 'closed' })) : []),
    ...(Array.isArray(closed?.web) ? closed.web.map((r) => ({ ...r, _scanner: 'web', _status: 'closed' })) : []),
  ];
  const acceptedRows = [
    ...(Array.isArray(accepted?.network)
      ? accepted.network.map((r) => ({ ...r, _scanner: 'network', _status: 'accepted' }))
      : []),
    ...(Array.isArray(accepted?.web)
      ? accepted.web.map((r) => ({ ...r, _scanner: 'web', _status: 'accepted' }))
      : []),
  ];
  return { openRows, closedRows, acceptedRows };
}

function buildTopVulns(openRows, limit = 10) {
  const grouped = new Map();
  for (const row of openRows) {
    const title = String(row?.title || '').trim();
    if (!title) continue;
    const severity = String(row?.severity || '').trim() || 'Unknown';
    const key = `${title}::${severity}`;
    const severityFloat = severityFloatOf(row);
    const current = grouped.get(key);
    if (!current) {
      grouped.set(key, {
        title,
        severity,
        severity_float: severityFloat,
        technical_impact: row?.technical_impact ?? null,
        business_impact: row?.business_impact ?? null,
        count: 1,
      });
      continue;
    }
    current.count += 1;
    if ((current.severity_float ?? -1) < severityFloat) current.severity_float = severityFloat;
  }
  return Array.from(grouped.values())
    .sort((a, b) => (Number(b.severity_float) || -1) - (Number(a.severity_float) || -1) || b.count - a.count)
    .slice(0, limit);
}

function isHostLabelPlaceholder(value) {
  const text = String(value ?? '').trim();
  if (!text) return true;
  const normalized = text.toLowerCase();
  return (
    normalized === 'na'
    || normalized === 'n/a'
    || normalized === 'none'
    || normalized === 'unknown'
    || normalized === '-'
    || normalized === '--'
  );
}

/** Prefer real hostname labels from finding rows (Risks uses hostname + host). */
export function pickFindingHostname(...values) {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    if (isHostLabelPlaceholder(value)) continue;
    return String(value).trim();
  }
  return '';
}

/**
 * Merge inventory hostname / uu_id onto finding-aggregated top hosts.
 * Counts stay from open findings; display fields come from InventoryDb when findings omit them.
 */
export function enrichTopHostsWithInventory(hosts, inventoryHosts) {
  const byIp = new Map();
  for (const row of Array.isArray(inventoryHosts) ? inventoryHosts : []) {
    const ip = String(row?.ip || '').trim();
    if (!ip || byIp.has(ip)) continue;
    byIp.set(ip, row);
  }
  return (Array.isArray(hosts) ? hosts : []).map((host) => {
    const ip = String(host?.ip || '').trim();
    const inv = byIp.get(ip);
    const hostname = pickFindingHostname(
      host?.hostname,
      inv?.hostname,
      inv?.host_name,
      inv?.name,
    );
    return {
      ...host,
      hostname,
      uu_id: inv?.uu_id || inv?.id || host?.uu_id || ip,
    };
  });
}

/** Aggregate open network findings by IP — same rows as the Risks list. */
export function buildTopHostsFromOpenFindings(openRows, limit = 10) {
  const byIp = new Map();
  for (const row of openRows) {
    if (row?._scanner !== 'network') continue;
    const ip = String(row?.ip || row?.ip_address || '').trim();
    if (!ip) continue;
    const bucket = severityBucket(row?.severity) || 'low';
    let host = byIp.get(ip);
    if (!host) {
      host = {
        uu_id: ip,
        ip,
        hostname: pickFindingHostname(row?.hostname, row?.host, row?.host_name),
        total_vul: 0,
        critical_vul: 0,
        high_vul: 0,
        medium_vul: 0,
        low_vul: 0,
      };
      byIp.set(ip, host);
    }
    if (!host.hostname) {
      host.hostname = pickFindingHostname(row?.hostname, row?.host, row?.host_name);
    }
    host.total_vul += 1;
    host[`${bucket}_vul`] = toCount(host[`${bucket}_vul`]) + 1;
  }
  return Array.from(byIp.values())
    .sort((a, b) => b.total_vul - a.total_vul || b.critical_vul - a.critical_vul)
    .slice(0, limit);
}

function countDatedRows(rows) {
  let dated = 0;
  for (const row of rows || []) {
    if (!Number.isNaN(findingDetectedMs(row))) dated += 1;
  }
  return dated;
}

/**
 * @param {{ openRows: any[], closedRows: any[], acceptedRows: any[] }} packs
 * @param {string} daysParam - "7" | "30" | "180" | "365" | "all"
 * @param {{ alreadyWindowed?: boolean, minDateCoverage?: number }} [options]
 * @returns {null | object} null when detection dates are unavailable (caller keeps API aggregates)
 */
export function buildOverviewWidgetsFromFindings(packs, daysParam, options = {}) {
  const days = overviewDaysParamToNumber(daysParam);
  if (days == null) return null;

  const alreadyWindowed = Boolean(options.alreadyWindowed);
  const minDateCoverage = Number.isFinite(options.minDateCoverage) ? options.minDateCoverage : 0.95;

  const openSource = packs?.openRows || [];
  const closedSource = packs?.closedRows || [];
  const acceptedSource = packs?.acceptedRows || [];
  const datedOpen = countDatedRows(openSource);
  const openCoverage = openSource.length ? datedOpen / openSource.length : 1;

  // Staging list payloads currently omit `date_time`; without it we cannot window KPIs
  // unless the server already applied `days` (alreadyWindowed).
  if (!alreadyWindowed && openSource.length > 0 && openCoverage < minDateCoverage) {
    return {
      unsupported: true,
      reason: 'missing_detection_dates',
      dateCoverage: openCoverage,
      summaryPatch: null,
      topVulnerabilities: null,
      topHosts: null,
    };
  }

  const openRows = alreadyWindowed ? openSource : filterFindingsByDays(openSource, daysParam);
  const closedCoverage = closedSource.length ? countDatedRows(closedSource) / closedSource.length : 1;
  const acceptedCoverage = acceptedSource.length ? countDatedRows(acceptedSource) / acceptedSource.length : 1;
  // Only window accepted/closed when those packs have dates; otherwise leave API totals.
  const closedRows =
    alreadyWindowed || closedCoverage >= minDateCoverage
      ? (alreadyWindowed ? closedSource : filterFindingsByDays(closedSource, daysParam))
      : null;
  const acceptedRows =
    alreadyWindowed || acceptedCoverage >= minDateCoverage
      ? (alreadyWindowed ? acceptedSource : filterFindingsByDays(acceptedSource, daysParam))
      : null;

  const severity = { critical: 0, high: 0, medium: 0, low: 0 };
  const networkSeverity = { critical: 0, high: 0, medium: 0, low: 0 };
  const webSeverity = { critical: 0, high: 0, medium: 0, low: 0 };
  let network = 0;
  let web = 0;

  for (const row of openRows) {
    const bucket = severityBucket(row?.severity);
    if (!bucket) continue;
    severity[bucket] += 1;
    if (row?._scanner === 'web') {
      web += 1;
      webSeverity[bucket] += 1;
    } else {
      network += 1;
      networkSeverity[bucket] += 1;
    }
  }

  const totalOpen = severity.critical + severity.high + severity.medium + severity.low;

  const summaryPatch = {
    total_open: totalOpen,
    total_count_site: totalOpen,
    open_count_site: totalOpen,
    critical_count_site: severity.critical,
    high_count_site: severity.high,
    medium_count_site: severity.medium,
    low_count_site: severity.low,
    critical: severity.critical,
    high: severity.high,
    medium: severity.medium,
    low: severity.low,
    total_network: network,
    total_web: web,
    net_count_site: network,
    web_count_site: web,
    critical_network: networkSeverity.critical,
    high_network: networkSeverity.high,
    medium_network: networkSeverity.medium,
    low_network: networkSeverity.low,
    critical_net_count_site: networkSeverity.critical,
    high_net_count_site: networkSeverity.high,
    medium_net_count_site: networkSeverity.medium,
    low_net_count_site: networkSeverity.low,
    critical_web: webSeverity.critical,
    high_web: webSeverity.high,
    medium_web: webSeverity.medium,
    low_web: webSeverity.low,
    critical_web_count_site: webSeverity.critical,
    high_web_count_site: webSeverity.high,
    medium_web_count_site: webSeverity.medium,
    low_web_count_site: webSeverity.low,
  };
  if (acceptedRows) {
    summaryPatch.accepted_count_site = acceptedRows.length;
    summaryPatch.total_accepted = acceptedRows.length;
  }
  if (closedRows) {
    summaryPatch.close_count_site = closedRows.length;
    summaryPatch.total_closed = closedRows.length;
  }

  return {
    unsupported: false,
    reason: null,
    summaryPatch,
    topVulnerabilities: buildTopVulns(openRows, 10),
    topHosts: buildTopHostsFromOpenFindings(openRows, 10),
    openCount: totalOpen,
    days,
  };
}

export function overviewRangeKeyToDays(rangeKey) {
  if (rangeKey === 'all_time') return null;
  return OVERVIEW_DATE_RANGE_DAYS[rangeKey] ?? null;
}

function parseTrendIncidentDateMs(raw) {
  const label = String(raw ?? '').trim();
  if (!label) return NaN;
  const dmy = label.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  const normalized = dmy ? `${dmy[3]}-${dmy[2]}-${dmy[1]}` : label;
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? NaN : ms;
}

/** Filter transformed trend incidents to the Overview picker window. */
export function filterTrendIncidentsByDays(incidents, daysParam) {
  const list = Array.isArray(incidents) ? incidents : [];
  const days = overviewDaysParamToNumber(daysParam);
  if (days == null) return list;
  let latestMs = Date.now();
  for (const inc of list) {
    const t = parseTrendIncidentDateMs(inc?.date);
    if (!Number.isNaN(t)) latestMs = Math.max(latestMs, t);
  }
  const cutoffMs = latestMs - days * 86400000;
  return list.filter((inc) => {
    const t = parseTrendIncidentDateMs(inc?.date);
    if (Number.isNaN(t)) return false;
    return t >= cutoffMs;
  });
}

function sumTrendSeverities(incidents) {
  const severity = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const inc of incidents || []) {
    severity.critical += toCount(inc?.critical);
    severity.high += toCount(inc?.high);
    severity.medium += toCount(inc?.medium);
    severity.low += toCount(inc?.low);
  }
  return severity;
}

function trendTotal(severity) {
  return severity.critical + severity.high + severity.medium + severity.low;
}

/**
 * Frontend-only fallback when vulnerability list rows omit detection dates.
 *
 * Scales all-time open KPI totals by (trend activity in window / all trend activity)
 * so shorter ranges cannot exceed All time. Does not rebuild hosts / top-vuln tables.
 */
export function buildOverviewWidgetsFromTrend(incidents, daysParam, baseSummary = null) {
  const days = overviewDaysParamToNumber(daysParam);
  if (days == null) return null;

  const allIncidents = Array.isArray(incidents) ? incidents : [];
  const windowed = filterTrendIncidentsByDays(allIncidents, daysParam);
  const allSev = sumTrendSeverities(allIncidents);
  const winSev = sumTrendSeverities(windowed);
  const allTrendTotal = trendTotal(allSev);
  const winTrendTotal = trendTotal(winSev);
  const overallScale = allTrendTotal > 0 ? Math.min(1, winTrendTotal / allTrendTotal) : 0;

  const scaleBucket = (key) => {
    const allBucket = allSev[key];
    if (allBucket > 0) return Math.min(1, winSev[key] / allBucket);
    return overallScale;
  };

  const baseCritical = toCount(baseSummary?.critical_count_site ?? baseSummary?.critical);
  const baseHigh = toCount(baseSummary?.high_count_site ?? baseSummary?.high);
  const baseMedium = toCount(baseSummary?.medium_count_site ?? baseSummary?.medium);
  const baseLow = toCount(baseSummary?.low_count_site ?? baseSummary?.low);
  const baseNet = toCount(baseSummary?.total_network ?? baseSummary?.net_count_site);
  const baseWeb = toCount(baseSummary?.total_web ?? baseSummary?.web_count_site);
  const baseOpen =
    baseCritical + baseHigh + baseMedium + baseLow
    || toCount(baseSummary?.total_open ?? baseSummary?.total_count_site)
    || baseNet + baseWeb;

  const critical = Math.round(baseCritical * scaleBucket('critical'));
  const high = Math.round(baseHigh * scaleBucket('high'));
  const medium = Math.round(baseMedium * scaleBucket('medium'));
  const low = Math.round(baseLow * scaleBucket('low'));
  let totalOpen = critical + high + medium + low;
  // Keep total aligned with scaled all-time open when severity buckets were empty on base.
  if (totalOpen === 0 && baseOpen > 0) {
    totalOpen = Math.round(baseOpen * overallScale);
  }

  const network = Math.round(baseNet * overallScale);
  const web = Math.round(baseWeb * overallScale);

  const summaryPatch = {
    total_open: totalOpen,
    total_count_site: totalOpen,
    open_count_site: totalOpen,
    critical_count_site: critical,
    high_count_site: high,
    medium_count_site: medium,
    low_count_site: low,
    critical,
    high,
    medium,
    low,
    total_network: network,
    total_web: web,
    net_count_site: network,
    web_count_site: web,
  };

  return {
    unsupported: false,
    source: 'trend',
    summaryPatch,
    topVulnerabilities: null,
    topHosts: null,
    openCount: totalOpen,
    days,
    scale: overallScale,
  };
}
